/**
 * SharedCartPage — View a shared cart/wishlist via share token.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Heart, Package, RefreshCw, Copy } from 'lucide-react';
import { useCart } from '@/hooks/useCart';
import { toast } from 'sonner';

const SharedCartPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['shared-cart', token],
    queryFn: async () => {
      // Try as cart first
      const { data: cart } = await pmsFrom('carts')
        .select('id, share_token')
        .eq('share_token', token)
        .maybeSingle();

      if (cart) {
        const { data: items } = await pmsFrom('cart_items')
          .select('*')
          .eq('cart_id', cart.id)
          .order('added_at');
        return { type: 'cart' as const, items: items || [] };
      }

      // Try as wishlist
      const { data: wishlist } = await pmsFrom('wishlists')
        .select('id, name, share_token')
        .eq('share_token', token)
        .eq('is_public', true)
        .maybeSingle();

      if (wishlist) {
        const { data: items } = await pmsFrom('wishlist_items')
          .select('*')
          .eq('wishlist_id', wishlist.id)
          .order('added_at');
        return { type: 'wishlist' as const, name: wishlist.name, items: items || [] };
      }

      return null;
    },
    enabled: !!token,
  });

  const { addItem } = useCart();

  const handleAddAllToCart = () => {
    if (!data?.items) return;
    data.items.forEach((item: any) => {
      addItem({
        product_id: item.product_id,
        product_name: item.product_name,
        product_image: item.product_image,
        product_price: item.product_price,
        product_url: item.product_url,
        quantity: item.quantity || 1,
        variation_id: item.variation_id || null,
        variation_attributes: item.variation_attributes || null,
        is_subscription: item.is_subscription || false,
        subscription_period: item.subscription_period || null,
        subscription_interval: item.subscription_interval || null,
      });
    });
    toast.success('All items added to your cart!');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg">This shared {data?.type || 'link'} is no longer available.</p>
        </div>
      </div>
    );
  }

  const isWishlist = data.type === 'wishlist';
  const total = data.items.reduce((sum: number, i: any) => sum + (i.product_price || 0) * (i.quantity || 1), 0);

  return (
    <>
      <SEOHead title={isWishlist ? `Shared Wishlist` : 'Shared Cart'} description="View shared products" />
      <div className="min-h-screen py-20">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            {isWishlist ? <Heart className="h-10 w-10 mx-auto mb-3 text-primary" /> : <ShoppingCart className="h-10 w-10 mx-auto mb-3 text-primary" />}
            <h1 className="text-3xl font-heading font-bold text-foreground">
              {isWishlist ? (data as any).name || 'Shared Wishlist' : 'Shared Cart'}
            </h1>
            <p className="text-muted-foreground mt-2">{data.items.length} items</p>
          </div>

          <div className="space-y-4">
            {data.items.map((item: any) => (
              <div key={item.id} className="flex gap-4 p-4 rounded-xl border border-border bg-card">
                {item.product_image && (
                  <img src={item.product_image} alt={item.product_name || ''} className="h-20 w-20 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground">{item.product_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {item.product_price != null && (
                      <span className="text-lg font-bold">${Number(item.product_price).toLocaleString()}</span>
                    )}
                    {item.quantity > 1 && <Badge variant="secondary">×{item.quantity}</Badge>}
                    {item.is_subscription && <Badge variant="secondary" className="gap-1"><RefreshCw className="h-2.5 w-2.5" />Subscription</Badge>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {data.items.length > 0 && (
            <div className="mt-8 space-y-4">
              <div className="flex justify-between text-lg font-bold border-t border-border pt-4">
                <span>Total</span>
                <span>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <Button size="lg" className="w-full gap-2" onClick={handleAddAllToCart}>
                <ShoppingCart className="h-4 w-4" />
                Add All to My Cart
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full gap-2"
                onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }}
              >
                <Copy className="h-4 w-4" />
                Copy Share Link
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SharedCartPage;
